/**
 * Image Configuration
 *
 * Centralized configuration for image dimensions and default presets.
 * Dimensions are extracted from CSS and component props.
 * Presets are preserved from source URLs or use defaults here.
 */

import type { ImagePreset } from '@/utils/transformed-image';

/**
 * Image dimensions extracted from CSS and component props
 */
export const IMAGE_DIMENSIONS = {
  // Banner images (fullscreen) — restored from ×0.8889
  BANNER: {
    FULLSCREEN: {
      width: 1920,
      height: 1080,
    },
  },

  // Show/Episode logos
  SHOW_LOGO: {
    HOME_BANNER: {
      width: 225,
      height: 135,
    },
    SHOW_PAGE_BANNER: {
      width: 166.52,
      height: 54,
    },
  },

  // Match/Competition logos
  MATCH_LOGO: {
    HOME_BANNER: {
      width: 225,
      height: 135,
    },
    SHOW_PAGE_BANNER: {
      width: 166.52,
      height: 54,
    },
    CARD: {
      width: 165.89,
      height: 54,
    },
  },

  // Competition simple logos
  COMPETITION_LOGO: {
    SIMPLE: {
      width: 166.52,
    },
  },

  // Team images
  TEAM_IMAGE: {
    BANNER: {
      width: 168.75,
      height: 168.75,
    },
    CARD: {
      width: 27,
      height: 27,
    },
  },

  // Watch Clubs card (type 23 & 53) - circular logo
  WATCH_CLUB: {
    LOGO: {
      width: 58,
      height: 58,
    },
  },

  // Club page banner - team logo
  CLUB_LOGO: {
    BANNER: {
      height: 155,
    },
  },

  // Card images
  CARD: {
    SHOW_CARD: {
      width: 265.52,
      height: 331.89,
    },
    MATCH_CARD_IMAGE: {
      width: 423.56,
      height: 173.25,
    },
    EPISODE_CARD_IMAGE: {
      width: 423.56,
      height: 173.25,
    },
  },
  AVATAR: {
    MENU: {
      width: 59,
      height: 59,
    },
  },
} as const;

/**
 * Default presets for image types
 * These are used only if preset cannot be extracted from source URL
 */
export const IMAGE_DEFAULT_PRESETS: Record<string, ImagePreset> = {
  BANNER: 'orj960',
  SHOW_LOGO: 'original',
  MATCH_LOGO: 'large',
  COMPETITION_LOGO: 'large',
  TEAM_IMAGE: 'original',
  WATCH_CLUB: 'original',
  CLUB_LOGO: 'original',
  SHOW_CARD: 'orj360',
  EPISODE_CARD: 'orj360',
  MATCH_CARD: 'orj360',
  VIDEO_CARD: 'orj360',
  AVATAR: 'original',
} as const;

/**
 * Helper to get dimensions for a specific image type and context
 */
export function getImageDimensions(
  type: keyof typeof IMAGE_DIMENSIONS,
  context?: string,
): { width: number; height: number } | null {
  const typeConfig = IMAGE_DIMENSIONS[type];
  if (!typeConfig) return null;

  if (context && typeConfig[context as keyof typeof typeConfig]) {
    const contextConfig = typeConfig[context as keyof typeof typeConfig] as { width: number; height: number };
    return contextConfig;
  }

  // Return first available context if no specific context provided
  const firstKey = Object.keys(typeConfig)[0];
  if (firstKey) {
    return typeConfig[firstKey as keyof typeof typeConfig] as { width: number; height: number };
  }

  return null;
}

/**
 * Helper to get default preset for an image type
 */
export function getDefaultPreset(type: string): ImagePreset {
  return IMAGE_DEFAULT_PRESETS[type] || 'original';
}
